import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  RotateCcw,
  XCircle,
  ArrowUpRight,
  FileDown,
  ShieldCheck,
  Download,
  Unlock,
  FileText,
  ChevronDown,
  Play,
} from 'lucide-react';
import type {
  ClarifyingSlot,
  PlanStep,
  PlanStepVerificationSummary,
  SmartStartStageRunRow,
  StageVerificationResult,
} from '../../../types/smartStart';
import VerificationPanel from '../VerificationPanel';
import {
  downloadArtifact,
  overrideVerificationGate,
  saveIntakeSlots,
  SessionRevisionConflictError,
  VerificationOverrideError,
} from '../../../services/smartStartApi';

export interface PlanStepCardDocument {
  name: string;
  size?: number | null;
  mime?: string | null;
  text?: string;
}

interface Props {
  sessionId: number;
  step: PlanStep;
  stageRun: SmartStartStageRunRow | null;
  isLast: boolean;
  isBusy: boolean;
  canResume: boolean;
  attachedDocuments?: PlanStepCardDocument[];
  clarifyingSlots?: ClarifyingSlot[];
  filledSlots?: Record<string, string>;
  onOpenStep: (stepIndex: number) => void;
  onReopenStep: (stepIndex: number) => void;
  onResumeStep: (stepIndex: number) => void;
  onRejectStep: (stepIndex: number, reason: string) => void;
  onVerificationUpdate: () => void;
}

const formatDocSize = (bytes?: number | null): string => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const TARGET_SCREEN_LABELS: Record<string, string> = {
  sop_builder: 'SOP Builder',
  ai_council: 'AI Council',
  process_maps: 'Process Maps',
  interviews: 'Interviews',
  risk_mapping: 'Risk Mapping',
  rcsa_process_steps: 'RCSA Process Steps',
  process_optimization: 'Process Optimization',
  bd_engagements: 'BD Pipeline',
  bd_engagement_detail: 'BD Pipeline',
  smart_data_analyst: 'Smart Data Analyst',
  data_cleaning: 'Data Cleaning',
  project_management: 'Project Management',
  intelligence: 'Jarvis Intelligence',
  inline_export: 'Export',
  inline_review: 'Review',
};

const FALLBACK_SLOT_META: Record<string, { label: string; prompt: string; placeholder: string }> = {
  audience: { label: 'Audience', prompt: 'Who is the primary audience for this deliverable?', placeholder: 'Executive sponsors, internal audit, regulator…' },
  template: { label: 'Template or format', prompt: 'Any specific template, structure, or format we should follow?', placeholder: 'Internal 13-section template, client-agreed format…' },
  scope: { label: 'Scope', prompt: 'What is in scope and what is out of scope?', placeholder: 'Applicable process / region / system…' },
  purpose: { label: 'Purpose', prompt: 'What is this deliverable for?', placeholder: 'Audit evidence, training, process documentation…' },
  compliance_framework: { label: 'Compliance framework', prompt: 'Which framework are we checking against?', placeholder: 'ISO 27001, SOC 2, NIST CSF, internal policy…' },
  time_horizon: { label: 'Time horizon', prompt: 'Over what timeframe?', placeholder: '6 months / 12 months / 3 years' },
};

const SUB_STEP_LABELS: Record<string, string> = {
  intake: 'Intake — upload discovery docs',
  synthesis: 'Discovery synthesis',
  ou_oa: 'OU/OA deck',
  plan: 'Project plan',
  datasim: 'DataSim pressure test',
  sow: 'SOW generator',
  won: 'PM handoff (deal won)',
  'review-summaries': 'Review document summaries',
  'approve-stakeholders': 'Approve stakeholders',
  'validate-process-map': 'Validate process map',
  'generate-sop': 'Generate SOP',
  'approve-terminology': 'Approve terminology',
  'fill-gaps': 'Fill gaps',
  'update-change': 'Update / change',
};

function humaniseSubStep(s?: string | null): string {
  if (!s) return '';
  return SUB_STEP_LABELS[s] || s.replace(/[-_]/g, ' ');
}

function VerificationSummaryPill({ summary }: { summary: PlanStepVerificationSummary | null }) {
  if (!summary) return null;
  if (summary.pending) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg text-xs">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Verifying in background — score will appear here in a moment.
      </div>
    );
  }
  if (!summary.ran) {
    if (summary.error) {
      return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-xs">
          <AlertTriangle className="w-3.5 h-3.5" />
          Verification skipped: {summary.error}
        </div>
      );
    }
    return null;
  }
  const score = typeof summary.composite_score === 'number' ? Math.round(summary.composite_score) : null;
  const band = summary.band || 'unknown';
  const bandColor =
    band === 'high' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : band === 'moderate' ? 'bg-amber-50 border-amber-200 text-amber-800'
    : band === 'low' ? 'bg-red-50 border-red-200 text-red-800'
    : 'bg-gray-50 border-gray-200 text-gray-700';
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg text-xs ${bandColor}`}>
      <ShieldCheck className="w-3.5 h-3.5" />
      Verified — {score != null ? `${score}% confidence` : 'ran'} · {summary.gaps_count || 0} gaps · {summary.remediation_items_count || 0} findings
    </div>
  );
}

const PlanStepCard: React.FC<Props> = ({ sessionId, step, stageRun, isLast, isBusy, canResume, attachedDocuments, clarifyingSlots, filledSlots, onOpenStep, onReopenStep, onResumeStep, onRejectStep, onVerificationUpdate }) => {
  const [rejectNotesOpen, setRejectNotesOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [downloadingRef, setDownloadingRef] = useState<string | null>(null);
  const [docsExpanded, setDocsExpanded] = useState(false);

  const missingSlotsList = (step.missing_slots || []).filter((k) => !!k);
  const missingSlotDetails = missingSlotsList.map((k) => {
    const meta = (clarifyingSlots || []).find((s) => s.key === k);
    const fallback = FALLBACK_SLOT_META[k];
    return {
      key: k,
      label: meta?.label || fallback?.label || k.replace(/_/g, ' '),
      prompt: meta?.prompt || fallback?.prompt || `Provide a value for "${k}"`,
      placeholder: meta?.placeholder || fallback?.placeholder || '',
    };
  });
  const [slotDraft, setSlotDraft] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const k of missingSlotsList) init[k] = (filledSlots && filledSlots[k]) || '';
    return init;
  });
  const [slotSaving, setSlotSaving] = useState(false);

  React.useEffect(() => {
    setSlotDraft((prev) => {
      const next: Record<string, string> = { ...prev };
      for (const k of missingSlotsList) {
        if (next[k] == null) next[k] = (filledSlots && filledSlots[k]) || '';
      }
      for (const k of Object.keys(next)) {
        if (!missingSlotsList.includes(k)) delete next[k];
      }
      return next;
    });
  }, [step.missing_slots?.join('|')]);

  const saveSlots = async () => {
    const trimmed: Record<string, string> = {};
    for (const k of missingSlotsList) {
      const v = (slotDraft[k] || '').trim();
      if (!v) {
        toast.error(`Please provide a value for ${k}.`);
        return;
      }
      trimmed[k] = v;
    }
    try {
      setSlotSaving(true);
      await saveIntakeSlots(sessionId, trimmed);
      toast.success('Saved — step unlocked.');
      onVerificationUpdate();
    } catch (err: any) {
      if (err instanceof SessionRevisionConflictError) {
        toast.error('Session updated elsewhere — refreshing.');
        onVerificationUpdate();
      } else {
        toast.error(`Could not save: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setSlotSaving(false);
    }
  };

  const isIntakeStep =
    step.module === 'intake' ||
    step.step_key === 'intake' ||
    step.target_screen === 'inline_review';
  const isVerifyingStep = step.verify_on_return === true;
  const showSourceDocs =
    Array.isArray(attachedDocuments) &&
    attachedDocuments.length > 0 &&
    (isIntakeStep || isVerifyingStep);
  const sourceDocsLabel = isIntakeStep
    ? 'Documents this step ingests'
    : 'Documents used for verification';

  const handleDownloadRef = async (ref: Record<string, any>, refKey: string) => {
    if (!ref?.url || typeof ref.url !== 'string') return;
    const fileName = typeof ref.label === 'string' && ref.label
      ? ref.label
      : `smart-router-artifact-${Date.now()}`;
    try {
      setDownloadingRef(refKey);
      await downloadArtifact(ref.url, fileName);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Download failed';
      toast.error(`Could not download: ${msg}`);
    } finally {
      setDownloadingRef(null);
    }
  };

  const screenLabel = TARGET_SCREEN_LABELS[step.target_screen] || step.target_screen;
  const status = step.status;
  const isInline = step.target_screen === 'inline_export' || step.target_screen === 'inline_review';

  const verificationResult: StageVerificationResult | null =
    (stageRun?.output_payload as any)?.verification || null;
  const stageOverride = verificationResult?.override || null;

  const exportBlockedPayload = (stageRun?.output_payload as any) || {};
  const isExportBlocked =
    step.target_screen === 'inline_export' &&
    exportBlockedPayload.export_blocked_by_verification === true;
  const lowStageDetails: Array<{
    stage_key: string;
    stage_index: number | null;
    stage_label: string;
    composite_score: number | null;
    band: string | null;
  }> = Array.isArray(exportBlockedPayload.low_stage_details)
    ? exportBlockedPayload.low_stage_details
    : [];

  const [exportOverrideOpen, setExportOverrideOpen] = useState<number | null>(null);
  const [exportOverrideReason, setExportOverrideReason] = useState('');
  const [exportOverrideChecked, setExportOverrideChecked] = useState(false);
  const [exportOverrideBusy, setExportOverrideBusy] = useState(false);

  const submitExportOverride = async (targetStageIndex: number) => {
    const trimmed = exportOverrideReason.trim();
    if (trimmed.length < 10) {
      toast.error('Please provide a justification of at least 10 characters.');
      return;
    }
    if (!exportOverrideChecked) {
      toast.error('Please confirm you understand the risk before proceeding.');
      return;
    }
    try {
      setExportOverrideBusy(true);
      await overrideVerificationGate(sessionId, targetStageIndex, trimmed);
      toast.success('Override recorded — try Generate export again.');
      setExportOverrideOpen(null);
      setExportOverrideReason('');
      setExportOverrideChecked(false);
      onVerificationUpdate();
    } catch (err: any) {
      if (err instanceof VerificationOverrideError) {
        toast.error(err.message);
      } else {
        toast.error(`Override failed: ${err?.response?.data?.error || err.message || err}`);
      }
    } finally {
      setExportOverrideBusy(false);
    }
  };

  const renderActions = () => {
    if (status === 'completed') {
      return (
        <div className="flex items-center gap-2">
          {!isInline && (
            <button
              onClick={() => onReopenStep(step.index)}
              disabled={isBusy}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-lavender/40 rounded-lg text-lavender hover:bg-lavender/5 disabled:opacity-50"
              title={`Re-open ${screenLabel} with this step's saved session loaded — review or extend it without losing data.`}
            >
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Re-open in {screenLabel}
            </button>
          )}
          {isLast && (
            <span className="text-xs text-gray-500">Plan complete</span>
          )}
        </div>
      );
    }
    if (status === 'rejected') {
      return (
        <button
          onClick={() => onOpenStep(step.index)}
          disabled={isBusy}
          className="flex items-center gap-2 px-4 py-2 bg-lavender text-white text-sm font-semibold rounded-lg hover:bg-lavender-deep disabled:opacity-50"
        >
          <RefreshCw className="w-4 h-4" />
          Retry step
        </button>
      );
    }
    if (isInline) {
      const hasMissingSlots = missingSlotsList.length > 0;
      return (
        <button
          onClick={() => onOpenStep(step.index)}
          disabled={isBusy || hasMissingSlots}
          title={hasMissingSlots ? 'Fill the inputs above first.' : undefined}
          className="flex items-center gap-2 px-4 py-2 bg-lavender text-white text-sm font-semibold rounded-lg hover:bg-lavender-deep disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          {step.target_screen === 'inline_export' ? 'Generate export' : 'Run inline'}
        </button>
      );
    }
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {canResume ? (
          <>
            <button
              onClick={() => onResumeStep(step.index)}
              disabled={isBusy}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              title="Pick up where you left off inside the target screen"
            >
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 fill-current" />}
              Resume in {screenLabel}
            </button>
            <button
              onClick={() => onOpenStep(step.index)}
              disabled={isBusy || (step.missing_slots && step.missing_slots.length > 0)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title={`Discard in-progress work and open ${screenLabel} fresh`}
            >
              <RotateCcw className="w-4 h-4" />
              Re-run from scratch
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => onOpenStep(step.index)}
              disabled={isBusy || (step.missing_slots && step.missing_slots.length > 0)}
              className="flex items-center gap-2 px-4 py-2 bg-lavender text-white text-sm font-semibold rounded-lg hover:bg-lavender-deep disabled:opacity-50"
            >
              {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              {status === 'in_progress' ? `Re-run in ${screenLabel}` : `Open in ${screenLabel}`}
            </button>
            {status === 'in_progress' && (
              <button
                onClick={() => onOpenStep(step.index)}
                disabled={isBusy || (step.missing_slots && step.missing_slots.length > 0)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="Discard in-progress work and start fresh"
              >
                <RotateCcw className="w-4 h-4" />
                Restart
              </button>
            )}
          </>
        )}
        <button
          onClick={() => setRejectNotesOpen((v) => !v)}
          disabled={isBusy}
          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <XCircle className="w-4 h-4" />
          Skip / reject
        </button>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
            Step {step.index + 1} · {screenLabel}
            {step.sub_step ? ` · ${humaniseSubStep(step.sub_step)}` : ''}
          </div>
          <h2 className="text-lg font-bold text-gray-900 mt-1">{step.label}</h2>
          {step.description && (
            <p className="text-sm text-gray-600 mt-1">{step.description}</p>
          )}
        </div>
        {step.status === 'completed' && (
          <div className="flex items-center gap-1 text-emerald-600 text-xs font-semibold">
            <CheckCircle2 className="w-4 h-4" /> Completed
          </div>
        )}
      </div>

      {missingSlotsList.length > 0 && step.status !== 'completed' && (
        <div className="p-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-900">
              This step needs a couple of inputs before it can run. Fill them in below — the step will unlock instantly.
            </div>
          </div>
          <div className="space-y-2.5">
            {missingSlotDetails.map((slot) => (
              <div key={slot.key}>
                <label className="block text-xs font-semibold text-amber-900 mb-1">
                  {slot.label}
                </label>
                <input
                  type="text"
                  value={slotDraft[slot.key] || ''}
                  onChange={(e) => setSlotDraft((p) => ({ ...p, [slot.key]: e.target.value }))}
                  onKeyDown={(e) => {
                    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                      e.preventDefault();
                      saveSlots();
                    }
                  }}
                  placeholder={slot.placeholder || slot.prompt}
                  disabled={slotSaving}
                  className="w-full px-2.5 py-1.5 text-xs rounded-md border border-amber-300 bg-white focus:border-amber-500 focus:ring-2 focus:ring-amber-200 focus:outline-none disabled:opacity-50"
                />
                {slot.prompt && (
                  <p className="text-[10px] text-amber-800 mt-0.5 italic">{slot.prompt}</p>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={saveSlots}
              disabled={slotSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-600 text-white rounded-md hover:bg-amber-700 transition disabled:opacity-50"
              title="Save and unlock this step (⌘/Ctrl + Enter)"
            >
              {slotSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              {slotSaving ? 'Saving…' : 'Save & unlock step'}
            </button>
          </div>
        </div>
      )}

      {showSourceDocs && attachedDocuments && (
        <div className="mb-4 bg-sky-50 border border-sky-200 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setDocsExpanded((v) => !v)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-sky-100/60 transition"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-sky-600 flex-shrink-0" />
              <span className="text-xs font-semibold text-sky-900">{sourceDocsLabel}</span>
              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-sky-200 text-sky-900">
                {attachedDocuments.length}
              </span>
            </div>
            <ChevronDown
              className={`w-3.5 h-3.5 text-sky-600 transition-transform ${
                docsExpanded ? 'rotate-180' : ''
              }`}
            />
          </button>
          {docsExpanded && (
            <ul className="px-3 pb-3 space-y-1">
              {attachedDocuments.map((d, i) => (
                <li
                  key={`${d.name}-${i}`}
                  className="flex items-center gap-2 px-2 py-1.5 bg-white border border-sky-100 rounded text-xs"
                >
                  <FileText className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" />
                  <span className="text-gray-900 truncate font-medium">{d.name}</span>
                  <span className="text-[10px] text-gray-500 flex-shrink-0 ml-auto">
                    {formatDocSize(d.size)}
                    {typeof d.text === 'string' && d.text.length > 0
                      ? ` · ${(d.text.length / 1000).toFixed(1)}k chars`
                      : ''}
                  </span>
                </li>
              ))}
              <li className="text-[10px] text-sky-700 italic px-1 pt-1">
                {isIntakeStep
                  ? 'These documents are fed to this step as discovery context.'
                  : 'Verification grounds claims against the text extracted from these documents.'}
              </li>
            </ul>
          )}
        </div>
      )}

      {step.artifact_ref && (
        <div className="mt-2 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-xs font-semibold text-gray-800 mb-1">Returned from {screenLabel}</div>
          {step.artifact_ref.summary && (
            <p className="text-xs text-gray-700 whitespace-pre-wrap">{step.artifact_ref.summary}</p>
          )}
          {Array.isArray(step.artifact_ref.refs) && step.artifact_ref.refs.length > 0 && (
            <ul className="mt-2 space-y-1">
              {step.artifact_ref.refs.map((ref, i) => {
                const refKey = `${i}-${ref?.label || ref?.kind || ''}`;
                const isDownloadable = typeof ref.url === 'string'
                  && ref.url.startsWith('/api/smart-start/');
                const isBusyRef = downloadingRef === refKey;
                return (
                  <li key={i} className="flex items-center gap-1.5 text-xs text-gray-700">
                    <ArrowUpRight className="w-3 h-3" />
                    {typeof ref.label === 'string' ? ref.label : (ref.kind || 'artifact')}
                    {isDownloadable ? (
                      <button
                        type="button"
                        onClick={() => handleDownloadRef(ref, refKey)}
                        disabled={isBusyRef}
                        className="inline-flex items-center gap-1 text-lavender hover:text-lavender-deep disabled:opacity-50 ml-1 underline"
                      >
                        {isBusyRef
                          ? <Loader2 className="w-3 h-3 animate-spin" />
                          : <Download className="w-3 h-3" />}
                        {isBusyRef ? 'Downloading…' : 'Download'}
                      </button>
                    ) : typeof ref.url === 'string' && ref.url ? (
                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-lavender underline ml-1"
                      >
                        open
                      </a>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div className="mt-2 mb-4 space-y-2">
        <VerificationSummaryPill summary={step.verification_summary} />
        {stageOverride && (
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border-2 border-amber-300 rounded-lg text-xs">
            <Unlock className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 text-amber-900">
              <div className="font-semibold">
                Verification gate overridden by{' '}
                {stageOverride.overridden_by_user_email || `user #${stageOverride.overridden_by_user_id}`}
              </div>
              <div className="text-amber-800 mt-0.5 line-clamp-2">
                Reason: {stageOverride.reason}
              </div>
            </div>
          </div>
        )}
      </div>

      {verificationResult && verificationResult.ran && stageRun && (
        <div className="mt-2 mb-4">
          <VerificationPanel
            sessionId={sessionId}
            stageIndex={stageRun.stage_index}
            verification={verificationResult}
            onUpdate={onVerificationUpdate}
          />
        </div>
      )}

      {isExportBlocked && (
        <div className="mt-2 mb-4 p-4 bg-gradient-to-r from-red-50 to-amber-50 border-2 border-red-300 rounded-xl space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-red-900">
                Export blocked — confidence below threshold
              </div>
              <div className="text-xs text-red-800 mt-1">
                Cumulative confidence is{' '}
                <span className="font-semibold">
                  {exportBlockedPayload.cumulative_confidence ?? 0}%
                </span>{' '}
                (minimum {exportBlockedPayload.threshold ?? 60}% required).
                {' '}
                {exportBlockedPayload.pending_findings > 0
                  ? `${exportBlockedPayload.pending_findings} verification finding(s) still pending review.`
                  : ''}
              </div>
            </div>
          </div>

          {lowStageDetails.length > 0 ? (
            <div className="space-y-2">
              <div className="text-xs font-semibold text-red-900 uppercase tracking-wide">
                Low-confidence stages
              </div>
              <ul className="space-y-2">
                {lowStageDetails.map((s) => {
                  const targetIdx = s.stage_index;
                  const isOpen = exportOverrideOpen === targetIdx;
                  return (
                    <li
                      key={s.stage_key}
                      className="p-3 bg-white border border-red-200 rounded-lg"
                    >
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900 truncate">
                            {s.stage_label}
                          </div>
                          <div className="text-[11px] text-gray-600">
                            Score:{' '}
                            <span className="font-semibold text-red-700">
                              {s.composite_score != null ? `${s.composite_score}%` : 'unknown'}
                            </span>
                            {s.band ? ` · band: ${s.band}` : ''}
                          </div>
                        </div>
                        {targetIdx != null && !isOpen && (
                          <button
                            type="button"
                            onClick={() => {
                              setExportOverrideOpen(targetIdx);
                              setExportOverrideReason('');
                              setExportOverrideChecked(false);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
                          >
                            <Unlock className="w-3.5 h-3.5" />
                            Accept risk &amp; override
                          </button>
                        )}
                      </div>

                      {isOpen && targetIdx != null && (
                        <div className="mt-3 space-y-2">
                          <label className="block text-xs font-semibold text-red-900">
                            Justification <span className="text-red-600">*</span>{' '}
                            <span className="font-normal text-red-700">
                              (min 10 chars, recorded in audit trail)
                            </span>
                          </label>
                          <textarea
                            value={exportOverrideReason}
                            onChange={(e) => setExportOverrideReason(e.target.value)}
                            rows={3}
                            placeholder="e.g. Demo content for client presentation — accuracy already confirmed offline by SME."
                            maxLength={2000}
                            disabled={exportOverrideBusy}
                            className="w-full p-2 text-sm rounded-lg border border-red-300 bg-white focus:border-red-500 focus:ring-2 focus:ring-red-200 focus:outline-none resize-none"
                          />
                          <label className="flex items-start gap-2 cursor-pointer p-2 bg-red-50 rounded-lg border border-red-200">
                            <input
                              type="checkbox"
                              checked={exportOverrideChecked}
                              onChange={(e) => setExportOverrideChecked(e.target.checked)}
                              disabled={exportOverrideBusy}
                              className="mt-0.5 w-4 h-4 text-red-600 border-red-300 rounded focus:ring-red-500"
                            />
                            <span className="text-xs text-red-900">
                              I understand the AI flagged confidence concerns with this output
                              and I take responsibility for proceeding with it.
                            </span>
                          </label>
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setExportOverrideOpen(null);
                                setExportOverrideReason('');
                                setExportOverrideChecked(false);
                              }}
                              disabled={exportOverrideBusy}
                              className="px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => submitExportOverride(targetIdx)}
                              disabled={
                                exportOverrideBusy ||
                                exportOverrideReason.trim().length < 10 ||
                                !exportOverrideChecked
                              }
                              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {exportOverrideBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Unlock className="w-3.5 h-3.5" />
                              )}
                              Override gate
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="text-[11px] text-red-700">
                After overriding, click <span className="font-semibold">Generate export</span>{' '}
                again. Each override is logged in the audit trail and surfaced in the export
                appendix.
              </div>
            </div>
          ) : (
            <div className="text-xs text-red-800">
              {exportBlockedPayload.error || 'Resolve verification findings on prior steps and try again.'}
            </div>
          )}
        </div>
      )}

      <div>{renderActions()}</div>

      {rejectNotesOpen && step.status !== 'completed' && step.status !== 'rejected' && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="text-xs font-semibold text-gray-700 mb-1">
            Why are you skipping or rejecting this step?
          </div>
          <textarea
            rows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Optional — recorded in the audit trail"
            className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:border-lavender focus:ring-2 focus:ring-lavender/20 focus:outline-none"
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              onClick={() => {
                onRejectStep(step.index, rejectReason.trim());
                setRejectNotesOpen(false);
                setRejectReason('');
              }}
              className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700"
            >
              Confirm reject
            </button>
            <button
              onClick={() => setRejectNotesOpen(false)}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanStepCard;
